/**
 * @fileoverview Multi-factor authentication verification page component.
 * Handles MFA verification using either email or TOTP (Time-based One-Time Password) methods.
 * Displays a 6-digit code input interface and manages the verification flow.
 */

import { useState, useEffect, useRef } from "react";
import {
    Button,
    Card,
    CardContent,
    Container,
    Box,
    Typography,
    TextField,
    Alert,
    LinearProgress,
    Tabs,
    Tab,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import EmailIcon from "@mui/icons-material/Email";
import PhonelinkLockIcon from "@mui/icons-material/PhonelinkLock";
import { APP_IMAGES } from "../../constants/AppImages";
import { ROUTES, LocalStorageVariables, intl } from "dmsbasereact";
import { APP_COLORS } from "../../theme/appTheme";
import { getBaseURL } from "../../build_config";

/**
 * MFA Verification Page component
 *
 * Provides a two-factor authentication verification interface where users can:
 * - Enter a 6-digit verification code from email or authenticator app
 * - Switch between email and TOTP methods (if both are enabled)
 * - Resend email codes with cooldown timer
 * - Auto-submit when all 6 digits are entered
 * - Paste codes from clipboard
 *
 * The component expects to receive authentication state from the login page via
 * React Router's location state, including the pending token and available MFA methods.
 *
 * @returns {JSX.Element} The MFA verification page interface
 */
export const MFAVerificationPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Estado recebido do LoginPage
    const { pendingToken, mfaMethods, emailMasked } = location.state || {};

    // States
    const [code, setCode] = useState(["", "", "", "", "", ""]);
    // Se tem múltiplos métodos, prioriza TOTP (mais seguro e não requer espera de email)
    // Se tem só um método, usa esse
    const [selectedMethod, setSelectedMethod] = useState(() => {
        if (mfaMethods?.length > 1) {
            // Múltiplos métodos: prioriza TOTP
            return mfaMethods.includes("totp") ? "totp" : "email";
        }
        // Único método: usa o disponível
        return mfaMethods?.[0] || "email";
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    // Timer só é relevante quando email foi enviado
    // Se só tem email OU se tem múltiplos e escolheu email (após enviar)
    const hasOnlyEmail = mfaMethods?.length === 1 && mfaMethods[0] === "email";
    const [timeLeft, setTimeLeft] = useState(hasOnlyEmail ? 300 : 0);
    const [canResend, setCanResend] = useState(true);
    const [emailCodeSent, setEmailCodeSent] = useState(hasOnlyEmail);

    // Refs para inputs
    const inputRefs = useRef([]);

    // Redirect se não tiver pending token
    useEffect(() => {
        if (!pendingToken) {
            navigate(ROUTES.Login);
        }
    }, [pendingToken, navigate]);

    // Timer de expiração
    useEffect(() => {
        if (timeLeft <= 0) return;

        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    setError(intl.get("CodeExpired").d("Code expired. Request a new code."));
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    // Auto-focus no primeiro input
    useEffect(() => {
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    const handleCodeChange = (index, value) => {
        // Aceita apenas números
        if (!/^\d*$/.test(value)) return;

        const newCode = [...code];
        newCode[index] = value.slice(-1); // Apenas último dígito
        setCode(newCode);
        setError("");

        // Auto-focus no próximo input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        // Auto-submit se completar 6 dígitos
        if (index === 5 && value) {
            const fullCode = [...newCode.slice(0, 5), value].join("");
            if (fullCode.length === 6) {
                setTimeout(() => handleVerify(fullCode), 100);
            }
        }
    };

    const handleKeyDown = (index, e) => {
        // Backspace
        if (e.key === "Backspace" && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        // Arrow keys
        if (e.key === "ArrowLeft" && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === "ArrowRight" && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
        const newCode = pastedData.split("").concat(Array(6).fill("")).slice(0, 6);
        setCode(newCode);

        // Focus no último input preenchido ou primeiro vazio
        const lastFilledIndex = Math.min(pastedData.length, 5);
        inputRefs.current[lastFilledIndex]?.focus();

        // Auto-submit se paste completo
        if (pastedData.length === 6) {
            setTimeout(() => handleVerify(pastedData), 100);
        }
    };

    const handleVerify = async (codeToVerify = null) => {
        const fullCode = codeToVerify || code.join("");

        if (fullCode.length !== 6) {
            setError(intl.get("EnterAllDigits").d("Please enter all 6 digits"));
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await fetch(`${getBaseURL()}/auth/mfa/verify`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    pending_token: pendingToken,
                    code: fullCode,
                    mfa_method: selectedMethod,
                }),
            });

            const data = await response.json();

            if (response.ok && data.access_token) {
                // Login bem-sucedido - salvar tokens
                sessionStorage.setItem(LocalStorageVariables.PageToken, JSON.stringify(data));
                sessionStorage.setItem("expirationSwalShow", "true");

                if (data.refresh_token) {
                    const refreshToken = {
                        refreshToken: data.refresh_token,
                        tokenUses: 0,
                    };
                    sessionStorage.setItem("refresh", JSON.stringify(refreshToken));
                }

                if (data.inactivity_timer) {
                    localStorage.setItem(
                        LocalStorageVariables.InactivityTimer,
                        data.inactivity_timer
                    );
                }

                // Redirecionar para home
                window.location.href = ROUTES.Home;
            } else {
                // Handle error response - detail can be a string or an object (e.g., rate limit response)
                let errorMessage;
                if (typeof data.detail === 'object' && data.detail !== null) {
                    errorMessage = data.detail.message || data.detail.error || intl.get("InvalidCode").d("Invalid code. Please try again.");
                } else {
                    errorMessage = data.detail || intl.get("InvalidCode").d("Invalid code. Please try again.");
                }
                setError(errorMessage);
                setCode(["", "", "", "", "", ""]);
                inputRefs.current[0]?.focus();
                setLoading(false);
            }
        } catch (error) {
            console.error("MFA verification error:", error);
            setError(intl.get("VerificationError").d("Error verifying code. Please try again."));
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!canResend) return;

        setCanResend(false);
        setLoading(true);
        setError("");

        try {
            const response = await fetch(`${getBaseURL()}/auth/mfa/resend`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    pending_token: pendingToken,
                }),
            });

            if (response.ok) {
                setTimeLeft(300); // Reset timer
                setCode(["", "", "", "", "", ""]);
                setEmailCodeSent(true);
                inputRefs.current[0]?.focus();

                // Permitir reenvio após 30 segundos
                setTimeout(() => setCanResend(true), 30000);
            } else {
                setError(intl.get("ResendError").d("Error resending code. Please try again."));
                setCanResend(true);
            }
        } catch (error) {
            console.error("Resend error:", error);
            setError(intl.get("ResendError").d("Error resending code. Please try again."));
            setCanResend(true);
        } finally {
            setLoading(false);
        }
    };

    const handleMethodChange = async (event, newValue) => {
        setSelectedMethod(newValue);
        setCode(["", "", "", "", "", ""]);
        setError("");
        inputRefs.current[0]?.focus();

        // Se o usuário escolher email e temos múltiplos métodos,
        // precisamos solicitar o envio do código por email
        if (newValue === "email" && mfaMethods?.length > 1) {
            await handleResend();
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    return (
        <Container
            className="mfa-verification-page"
            maxWidth="false"
            disableGutters
            sx={{
                background: "#FFFFFF",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    maxWidth: "500px",
                    px: 2,
                }}
            >
                {/* Logo */}
                <Box sx={{ mb: 4, textAlign: "center" }}>
                    <Box
                        component="img"
                        src={APP_IMAGES.LOGO}
                        alt="Lena Logo"
                        sx={{
                            width: "120px",
                            height: "auto",
                            mb: 2,
                            filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.3))",
                        }}
                    />
                </Box>

                {/* Card */}
                <Card
                    sx={{
                        width: "100%",
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                        borderRadius: 3,
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                textAlign: "center",
                                mb: 1,
                                fontWeight: 600,
                                color: APP_COLORS.neutral.gray900,
                            }}
                        >
                            {intl.get("TwoStepVerification").d("Two-Step Verification")}
                        </Typography>

                        {/* Tabs (se tiver mais de um método) */}
                        {mfaMethods?.length > 1 && (
                            <Tabs
                                value={selectedMethod}
                                onChange={handleMethodChange}
                                variant="fullWidth"
                                sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
                            >
                                {mfaMethods.includes("email") && (
                                    <Tab
                                        icon={<EmailIcon />}
                                        label={intl.get("Email").d("Email")}
                                        value="email"
                                    />
                                )}
                                {mfaMethods.includes("totp") && (
                                    <Tab
                                        icon={<PhonelinkLockIcon />}
                                        label={intl.get("Authenticator").d("Authenticator")}
                                        value="totp"
                                    />
                                )}
                            </Tabs>
                        )}

                        {/* Descrição */}
                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: "center",
                                mb: 3,
                                color: APP_COLORS.neutral.gray600,
                            }}
                        >
                            {selectedMethod === "email"
                                ? (emailCodeSent
                                    ? intl.get("EnterEmailCode", { email: emailMasked }).d(
                                          `Enter the 6-digit code sent to ${emailMasked}`
                                      )
                                    : intl.get("ClickToSendEmailCode").d(
                                          "Click 'Send code' to receive the code by email"
                                      ))
                                : intl.get("EnterAuthenticatorCode").d(
                                      "Enter the code from your authenticator app"
                                  )}
                        </Typography>

                        {/* Error Alert */}
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Code Input */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1.5,
                                justifyContent: "center",
                                mb: 3,
                            }}
                        >
                            {code.map((digit, index) => (
                                <TextField
                                    key={index}
                                    inputRef={(el) => (inputRefs.current[index] = el)}
                                    value={digit}
                                    onChange={(e) => handleCodeChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onPaste={index === 0 ? handlePaste : undefined}
                                    disabled={loading || timeLeft === 0}
                                    inputProps={{
                                        maxLength: 1,
                                        style: {
                                            textAlign: "center",
                                            fontSize: "28px",
                                            fontWeight: "bold",
                                            padding: "16px 8px",
                                        },
                                    }}
                                    sx={{
                                        width: "60px",
                                        "& .MuiOutlinedInput-root": {
                                            "&.Mui-focused fieldset": {
                                                borderColor: APP_COLORS.primary.main,
                                                borderWidth: 2,
                                            },
                                        },
                                    }}
                                />
                            ))}
                        </Box>

                        {/* Timer - só mostra se email foi enviado */}
                        {selectedMethod === "email" && emailCodeSent && timeLeft > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {intl.get("ExpiresIn").d("Expires in")}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {formatTime(timeLeft)}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={(timeLeft / 300) * 100}
                                    sx={{ height: 6, borderRadius: 3 }}
                                />
                            </Box>
                        )}

                        {/* Verify Button */}
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => handleVerify()}
                            disabled={
                                loading ||
                                code.join("").length !== 6 ||
                                (selectedMethod === "email" && !emailCodeSent) ||
                                (selectedMethod === "email" && emailCodeSent && timeLeft === 0)
                            }
                            sx={{
                                backgroundColor: APP_COLORS.primary.main,
                                height: "50px",
                                textTransform: "none",
                                fontSize: "16px",
                                fontWeight: "bold",
                                borderRadius: 2,
                                mb: 2,
                                "&:hover": {
                                    backgroundColor: APP_COLORS.primary.dark,
                                },
                            }}
                        >
                            {loading ? intl.get("Verifying").d("Verifying...") : intl.get("Verify").d("Verify")}
                        </Button>

                        {/* Resend/Send Button (apenas para email) */}
                        {selectedMethod === "email" && (
                            <Button
                                variant={emailCodeSent ? "text" : "contained"}
                                fullWidth
                                onClick={handleResend}
                                disabled={!canResend || loading}
                                sx={{
                                    textTransform: "none",
                                    color: emailCodeSent ? APP_COLORS.primary.main : undefined,
                                    backgroundColor: emailCodeSent ? undefined : APP_COLORS.primary.main,
                                    mb: 2,
                                    "&:hover": emailCodeSent ? undefined : {
                                        backgroundColor: APP_COLORS.primary.dark,
                                    },
                                }}
                            >
                                {emailCodeSent
                                    ? intl.get("ResendCode").d("Resend code")
                                    : intl.get("SendCode").d("Send code by email")}
                            </Button>
                        )}

                        {/* Back to Login */}
                        <Button
                            variant="text"
                            fullWidth
                            onClick={() => navigate(ROUTES.Login)}
                            sx={{
                                textTransform: "none",
                                color: APP_COLORS.neutral.gray600,
                            }}
                        >
                            {intl.get("BackToLogin").d("Back to Login")}
                        </Button>
                    </CardContent>
                </Card>

                {/* Footer */}
                <Box sx={{ mt: 4, textAlign: "center" }}>
                    <Typography variant="body2" sx={{ color: APP_COLORS.neutral.gray500 }}>
                        {intl
                            .get("CopyrightLena", { year: new Date().getFullYear() })
                            .d("© {year} Lena. All rights reserved.")}
                    </Typography>
                </Box>
            </Box>
        </Container>
    );
};
